import React from 'react';
import { Checkbox, Tooltip, Typography } from '@mui/material';
import moment from 'moment';

import { genHtmlOptions, genMenuItems } from '../utils';
import validURL from '../utils/validators/url';

export const TRIP_TYPE_TOUR = 0;
export const TRIP_TYPE_BUS = 1;
export const TRIP_TYPE_EXHIBITION = 2;

export const POSITIVE_FEEDBACK_TARGETS = {};
export const TARGET_OTA_OR_URL = 0;
export const TARGET_OTA_OR_NONE = 1;
export const TARGET_URL = 2;
export const TARGET_NONE = 3;
POSITIVE_FEEDBACK_TARGETS[TARGET_OTA_OR_URL] = 'OTA if possible, URL otherwise';
POSITIVE_FEEDBACK_TARGETS[TARGET_OTA_OR_NONE] = 'OTA if possible, no review otherwise';
POSITIVE_FEEDBACK_TARGETS[TARGET_URL] = 'URL';
POSITIVE_FEEDBACK_TARGETS[TARGET_NONE] = 'No review';

export const positiveFeedbackTargetsMenuItems = genMenuItems(POSITIVE_FEEDBACK_TARGETS);
export const positiveFeedbackTargetsOptions = genHtmlOptions(POSITIVE_FEEDBACK_TARGETS);

export const validateItem = (pti) => {
    let canSave = true;
    const errors = {};
    if (!pti.item || pti.item.constructor !== Object) {
        if (pti._changed_item) {
            errors.item = { error: true, helperText: 'Item is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

export const formatNewUserManagerPushURL = (search_filter_list, record_list_filters, trip_id, user_id) => {
    //Delete unused record list filters
    for (let index = 0; index < Object.keys(record_list_filters).length; index++) {
        let key = Object.keys(record_list_filters)[index];

        if (!record_list_filters[key] || record_list_filters[key].length === 0) {
            delete record_list_filters[key];
        }
    }

    if (user_id) {
        if (search_filter_list.length > 0 || Object.keys(record_list_filters).length > 0) {
            return `/trip/${trip_id}/users/manager/${user_id}/${JSON.stringify({ searchFilterList: search_filter_list, recordListFilters: record_list_filters })}`;
        }
        return `/trip/${trip_id}/users/manager/${user_id}`;
    }

    if (search_filter_list.length > 0 || Object.keys(record_list_filters).length > 0) {
        return `/trip/${trip_id}/users/manager/${JSON.stringify({ searchFilterList: search_filter_list, recordListFilters: record_list_filters })}`;
    }
    return `/trip/${trip_id}/users/manager`;
};

export const groupUsersByRole = (tour) => {
    // const gbr = {};
    // (tour.users || []).forEach(u => {
    //     const brid = u.role ? u.role.id : 0;
    //     if (!gbr[brid]) { gbr[brid] = [] }
    //     gbr[brid].push(u);
    // });
    // return gbr;

    return (tour.users || []).reduce((gbr, u) => {
        const brid = u.role ? u.role.id : 0;
        if (!gbr[brid]) {
            gbr[brid] = [];
        }
        gbr[brid].push(u);
        return gbr;
    }, {});
};

export const getStaffUsers = (groupedByRole) => {
    return Object.keys(groupedByRole).reduce((list, roleId) => (roleId > 0 ? list.concat(groupedByRole[roleId]) : list), []);
};

export const getRegularUsers = (groupedByRole) => groupedByRole[0] || [];

export class TourUsers {
    constructor(tour) {
        this.tour = tour;
        this.gbr = groupUsersByRole(this.tour);
    }
    staffUsers() {
        return getStaffUsers(this.gbr);
    }
    regularUsers() {
        return getRegularUsers(this.gbr);
    }
}

//Convert additional custom ids into objects and add an idx field.
// Runs on every load and on every save response, so `_stored_managed` is what the server holds --
// a managed row there cannot be removed in the editor -- and `_stored_row` the managed row that
// undoing a take-over restores (AdditionalExternalIDManager). TripEdit re-runs this on rows it
// already reformatted, so the snapshot leaves the UI-only keys out.
export const reformatCustomIds = (trip_data) => {
    let new_trip = trip_data;
    let idx_counter = -1;
    if (new_trip.additional_custom_ids.length > 0) {
        new_trip.additional_custom_ids = new_trip.additional_custom_ids.map((aci) => {
            idx_counter = idx_counter + 1;
            const { idx, _stored_managed, _stored_row, ...stored } = aci;
            return {
                ...stored,
                idx: idx_counter,
                _stored_managed: !!stored.managed,
                ...(stored.managed ? { _stored_row: stored } : {}),
            };
        });
    }
    return new_trip;
};

export const removeUnusedTagsFromContextFields = (context) => {
    if (!context) {
        return context;
    }
    //Use reg exp to check if one of the string fields has empty tags in it and if so remove them from the context object
    let new_context = { ...context };
    let context_fields = Object.keys(context);

    for (let index = 0; index < context_fields.length; index++) {
        let key = context_fields[index];
        // Only process string fields, skip non-string values (objects, arrays, etc.)
        if (new_context[key] && 
            new_context[key] !== true && 
            typeof new_context[key] === 'string' &&
            new_context[key].replace(/(<\/?[^>]+(>|$)|&nbsp;|\s)/g, '').trim() === '') {
            delete new_context[key];
        }
    }
    return new_context;
};

export const removeTransportationToggle = (itinerary) => {
    if (!itinerary || !itinerary.days) return itinerary;
    let itinerary_data = { ...itinerary };
    for (let index = 0; index < itinerary_data.days.length; index++) {
        let timeslots_data = itinerary_data.days[index].timeslots;

        for (let index_a = 0; index_a < timeslots_data.length; index_a++) {
            if (timeslots_data[index_a].show_transport && !timeslots_data[index_a].transport_text && !timeslots_data[index_a].next_transport) {
                timeslots_data[index_a].show_transport = false;
            }
        }
    }

    return itinerary_data;
};

export const addTransportationToAllActivities = (itinerary) => {
    if (!itinerary || !itinerary.days) return itinerary;
    let itinerary_data = { ...itinerary };
    for (let index = 0; index < itinerary_data.days.length; index++) {
        let timeslots_data = itinerary_data.days[index].timeslots;

        for (let index_a = 0; index_a < timeslots_data.length; index_a++) {
            if (timeslots_data[index_a].next_transport) {
                let activities_data = timeslots_data[index_a].activities;
                for (let index_b = 0; index_b < activities_data.length; index_b++) {
                    activities_data[index_b].next_transport = timeslots_data[index_a].next_transport;
                    activities_data[index_b].transport_text = timeslots_data[index_a].transport_text;
                }
            }
        }
    }

    return itinerary_data;
};

export const hideIfTimeLabelTextNull = (itinerary) => {
    if (!itinerary || !itinerary.days) return itinerary;
    let itinerary_data = { ...itinerary };
    for (let index = 0; index < itinerary_data.days.length; index++) {
        let timeslots_data = itinerary_data.days[index].timeslots;

        for (let index_a = 0; index_a < timeslots_data.length; index_a++) {
            if (timeslots_data[index_a].timeline_display === 0 && !timeslots_data[index_a].timeline_text) {
                timeslots_data[index_a].timeline_display = 2;
            }
        }
    }

    return itinerary_data;
};

export const removeEmptyActivities = (itinerary) => {
    if (!itinerary || !itinerary.days) return itinerary;
    let itinerary_data = { ...itinerary };
    for (let index = 0; index < itinerary_data.days.length; index++) {
        let timeslots_data = itinerary_data.days[index].timeslots;

        for (let index_a = 0; index_a < timeslots_data.length; index_a++) {
            timeslots_data[index_a].activities = timeslots_data[index_a].activities.filter((a) => a.activity.id);
        }
    }

    return itinerary_data;
};

export const TagCheckbox = ({ cat, handleCheckbox, field, disabled, checked, label, tooltip }) => {
    const text = <Typography style={{ marginLeft: '1rem', fontSize: '80%', color: 'gray' }}>{label}</Typography>;
    return (
        <div style={{ display: 'flex', alignItems: 'center' }} data-field={field}>
            <Checkbox color="primary" disabled={disabled} checked={checked} onChange={(ev) => handleCheckbox(ev, cat.idx, field)} /> {tooltip ? <Tooltip title={tooltip} describeChild>{text}</Tooltip> : text}
        </div>
    );
};

export const validateRequiredTripConditions = async (trip, hasChanged, clientErrors, setClientErrors, enqueueSnackbar) => {
    let can_save = true;
    let enqueue_message = '';
    let enqueue_type = 'default';


    if (!hasChanged) {
        enqueue_message = 'No changes were detected for saving';
    }

    if (!trip.name || trip.name.trim() === '') {
        enqueue_message = 'There has to be a name for the trip';
        enqueue_type = 'error';
        setClientErrors((prev) => ({ ...prev, missingTripName: true }));
    }

    if (clientErrors.missingAdditionalCustomIDsValues) {
        enqueue_message = 'All custom ids must contain a value';
        enqueue_type = 'error';
    }

    if (!trip.start_timezone) {
        enqueue_message = 'A timezone must be selected for the trip';
        enqueue_type = 'error';
    }

    if (!trip.is_bespoke && clientErrors.missingWeeklySchedule) {
        enqueue_message = 'There has to be at least one day selected in the date scheduler';
        enqueue_type = 'error';
    }

    if (clientErrors.amountsNotEntered) {
        enqueue_message = 'Invalid activity price amounts in trip flow editor';
        enqueue_type = 'error';
    }

    if (clientErrors.activityNotSelected) {
        enqueue_message = 'An activity was added to a timeslot but not selected';
        enqueue_type = 'error';
    }

    if (!trip.is_bespoke && trip.tag_categories.filter((tc) => tc.category?.code === 'departure').length > 0 && trip.departures.length < 1) {
        enqueue_message = 'All required tags must have values';
        enqueue_type = 'error';
    }

    if (trip.pre_trip_items.filter((pti) => !pti.item?.id).length > 0) {
        enqueue_message = 'All newly created trip items must have items selected';
        enqueue_type = 'error';
    }

    if (clientErrors.invalidURLMeetingPointURL) {
        enqueue_message = 'Meeting point URL must be valid';
        enqueue_type = 'error';
    }
    
    if (trip.meeting_point?.maps_url && !trip.meeting_point?.name) {
        enqueue_message = 'Meeting point name is required';
        enqueue_type = 'error';
    }

    if (!trip.is_bespoke && trip.has_departures && trip.departures.length < 1) {
        enqueue_message = 'Associated Tag Category Departure selected but no Trip Departures added';
        enqueue_type = 'error';
        setClientErrors((prev) => ({ ...prev, isMissingDeparture: true }));
    }

    if (trip.tag_categories.filter((tc) => tc.category === null).length > 0) {
        enqueue_message = 'All associated tag categories must have a category selected';
        enqueue_type = 'error';
    }

    if (clientErrors.invalidURLReview) {
        enqueue_message = 'Review URL must be valid';
        enqueue_type = 'error';
    }

    if (clientErrors.invalidURLPhotoSharing) {
        enqueue_message = 'Photo Sharing URL must be valid';
        enqueue_type = 'error';
    }

    if ([TARGET_URL].indexOf(+trip.positive_review_target) >= 0) {
        if (!trip.positive_review_url) {
            enqueue_message = 'There has to be a positive review URL for the trip';
            enqueue_type = 'error';
            setClientErrors((prev) => ({ ...prev, missingPositiveReviewUrl: true }));
        }
    }

    if ([TARGET_OTA_OR_URL, TARGET_URL].indexOf(+trip.positive_review_target) >= 0) {
        if (!validURL(trip.positive_review_url)) {
            enqueue_message = 'Positive review URL must be valid';
            enqueue_type = 'error';
            setClientErrors((prev) => ({ ...prev, positive_review_url: true }));
        }
    }

    if (trip.context?.upsell_link && !validURL(trip.context.upsell_link)) {
        enqueue_message = 'Upsell link URL must be valid';
        enqueue_type = 'error';
    }

    // CP-6763: a custom map pin number the user erased (TripEditFlowTimeSlot)
    const timeslots = (trip.itinerary?.days || []).flatMap((day) => day?.timeslots || []);
    if (timeslots.some((ts) => (ts.pin_type || 'numbered') === 'numbered' && ts.pin_number_mode === 'custom' && ts._pin_custom_number_empty)) {
        enqueue_message = 'Custom map pin number cannot be empty';
        enqueue_type = 'error';
    }

    if (enqueue_message) {
        enqueueSnackbar(enqueue_message, { variant: enqueue_type })
        can_save = false;
    }

    return can_save;
};


export const getTimeFromDatetime = (datetime) => {
    if (!datetime) {
        return;
    }
    let new_time = datetime.split('T')[1]
    new_time = new_time.split('Z')[0]
    return new_time
}



export const getLocalDateFromUTC = (date, time, timezone) => {
    if (!date || !time || !timezone) {
        return;
    }

    const utcDateTimeString = `${date}T${time}Z`; // The 'T' and 'Z' designate a UTC datetime in ISO 8601 format
    const localDate = moment.utc(utcDateTimeString).tz(timezone).format('YYYY-MM-DD');
    return localDate;
}

// these constants and related options and menu items are outdated and should be removed
export const BOOKING_STATES = {};
export const BOOKING_NOT_REQUESTED = 0;
export const BOOKING_REQUESTED = 1;
export const BOOKING_CONFIRMED = 2;
BOOKING_STATES[BOOKING_NOT_REQUESTED] = 'Booking not requested';
BOOKING_STATES[BOOKING_REQUESTED] = 'Booking requested';
BOOKING_STATES[BOOKING_CONFIRMED] = 'Booking confirmed';

export const bookingStateMenuItems = genMenuItems(BOOKING_STATES);
export const bookingStateHtmlOptions = genHtmlOptions(BOOKING_STATES);
export const bookingStateEditHtmlOptions = genHtmlOptions(BOOKING_STATES, { [BOOKING_NOT_REQUESTED]: true });

export const ONTRIP_DOCUMENTS_TYPES = {};
export const ONTRIP_DOCUMENTS_INHERIT = 0;
export const ONTRIP_DOCUMENTS_OVERRIDE = 1;
ONTRIP_DOCUMENTS_TYPES[ONTRIP_DOCUMENTS_INHERIT] = 'Use activity documents';
ONTRIP_DOCUMENTS_TYPES[ONTRIP_DOCUMENTS_OVERRIDE] = 'Upload custom documents';

export const ADD_TRIP_USER_CLIENT_ERRORS = {};
ADD_TRIP_USER_CLIENT_ERRORS['validEmailAddress'] = 'Please enter a valid email address';

export const ConfirmationDialogText = {
    delete_trip: { title: 'Please confirm', body: 'Do you really want to delete this trip?', okButtonText: 'Delete', cancelButtonText: 'Cancel' },
    delete_trips: { title: 'Please confirm', body: 'Do you really want to delete these trips?', okButtonText: 'Delete', cancelButtonText: 'Cancel' },
    delete_cover_image: { title: 'Please confirm', body: 'Do you really want to delete this cover image?', okButtonText: 'Delete', cancelButtonText: 'Cancel' },
    delete_meeting_point_photo: { title: 'Please confirm', body: 'Do you really want to delete the meeting point photo?', okButtonText: 'Delete', cancelButtonText: 'Cancel' },
    deactivate_checkin_flow: { 
        title: 'Deactivate pre-check-in flow for this trip?', 
        body: (
            <>
                You're about to turn off the pre-check-in form feature.
                <br />
                <br />
                Your current setup will be saved and restored if you turn it back on later.
            </>
        ), 
        okButtonText: 'DEACTIVATE',
        cancelButtonText: 'CANCEL'
    },
    // CP-6579: Add Waiver prompts. "Used" only ever meant "already on this
    // pre-check-in" (each waiver goes on a form once; it can be on any number
    // of trips), and the same alert also fired when no waiver existed at all.
    no_waivers: {
        title: 'No waivers to add',
        body: (
            <>
                You haven't created any waivers yet.
                <br />
                <br />
                Create one under Settings &rsaquo; Waivers, then come back here to add it to the pre-check-in.
            </>
        ),
        okButtonText: 'GO TO WAIVERS',
        cancelButtonText: 'CANCEL',
    },
    no_enabled_waivers: {
        title: 'No waivers to add',
        body: (
            <>
                All of your waivers are disabled, so there is nothing to add.
                <br />
                <br />
                Enable one under Settings &rsaquo; Waivers, or create a new one, then come back here to add it to the pre-check-in.
            </>
        ),
        okButtonText: 'GO TO WAIVERS',
        cancelButtonText: 'CANCEL',
    },
    all_waivers_added: {
        title: 'All waivers already added',
        body: (
            <>
                Every waiver in Settings &rsaquo; Waivers is already on this pre-check-in, so there is nothing left to add.
                <br />
                <br />
                Create another waiver in Settings to add more.
            </>
        ),
        okButtonText: 'GO TO WAIVERS',
        cancelButtonText: 'CANCEL',
    },
    waivers_load_failed: {
        title: "Couldn't load waivers",
        body: 'The list of waivers could not be loaded, so nothing can be added right now.',
        okButtonText: 'TRY AGAIN',
        cancelButtonText: 'CANCEL',
    },
};
// `destructive`: styled as a DELETE (CP-5545) but labelled Remove, since the user leaves the trip, not the platform.
export const ConfirmationUserDialogText = {
    delete_user: { title: 'Please confirm', body: 'Do you really want to remove this user?', okButtonText: 'Remove', cancelButtonText: 'Cancel', destructive: true },
    delete_users: { title: 'Please confirm', body: 'Do you really want to remove these users?', okButtonText: 'Remove', cancelButtonText: 'Cancel', destructive: true },
};

export const extractStartAndEndDate = (inputString) => {
    if (!inputString) {
        return;
    }
    // Split the input string into its components
    const lines = inputString.split('\n');
    let startDate = null;
    let endDate = null;

    // Loop through each component to find and format dates
    lines.forEach(line => {
        if (line.startsWith('DTSTART:')) {
            // Extract and format the start date
            startDate = moment(line.substring(8), 'YYYYMMDDTHHmmssZ').format('YYYY-MM-DD');
        } else if (line.includes('UNTIL=')) {
            // Find the UNTIL component and extract and format the end date
            const untilIndex = line.indexOf('UNTIL=') + 6; // +6 to start after 'UNTIL='
            endDate = moment(line.substring(untilIndex, untilIndex + 15), 'YYYYMMDDTHHmmssZ').format('YYYY-MM-DD');
        }
    });

    // Return the formatted dates
    return { startDate, endDate };
}



export const getCurrencyFromActivities = (trip, defaultCurrency = 1) => {
    if (!trip?.itinerary?.days) return defaultCurrency;

    for (let day of trip.itinerary.days) {
        if (day.timeslots) {
            for (let timeslot of day.timeslots) {
                if (timeslot.activities) {
                    for (let activity of timeslot.activities) {
                        if (activity.currency) {
                            return activity.currency;
                        }
                    }
                }
            }
        }
    }
    return defaultCurrency;
};


export const checkTripItemsVisibility = (items, enqueueSnackbar) => {
    if (!items || items?.length === 0) {
        return false;
    }
    for (let index = 0; index < items.length; index++) {
        if (!items[index].all_users && 
            !items[index].all_travelers && 
            (!items[index].users || items[index].users?.length === 0) && 
            items[index].roles.length === 0 && 
            items[index].agroups?.length === 0) {
            enqueueSnackbar('Every trip item must be visible', { variant: 'error' });
            return true;
        }
    }
    return false;
}